<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import Tag        from 'primevue/tag'
import Select     from 'primevue/select'
import Textarea   from 'primevue/textarea'
import Chip       from 'primevue/chip'
import AppDialog  from '@/components/ui/AppDialog.vue'
import api        from '@/api'
import { useUserStore } from '@/stores/user'
import { useNotify }    from '@/services/useNotify'
import { useI18n }      from '@/i18n'
import { useConfirmDelete } from '@/composables/useConfirmDelete'
import { useFormatDate }    from '@/composables/useFormatDate'
import { flagFromISO2, regionNameFromISO2, normalizeISO2 } from '@/utils/country'

const emit = defineEmits(['filters-change'])

const user    = useUserStore()
const notify  = useNotify()
const { t, activeLang } = useI18n()
const { confirmDelete } = useConfirmDelete()
const { fmtDate } = useFormatDate()

// ── Table state ───────────────────────────────────────────────────────────────
const items   = ref([])
const total   = ref(0)
const loading = ref(false)
let   lastQuery = {}

// ── Filter state ──────────────────────────────────────────────────────────────
const filters = ref({
  country:           null,   // ISO-2 (or raw string)
  state_sponsor:     null,   // explicit sponsor
  has_state_sponsor: null,   // null | true | false
  is_raas:           null,   // null | true | false
  has_ttps:          null,   // null | true | false
  has_tools:         null,   // null | true | false
  // Set via the stats widgets next to the table (donut/bar click)
  activity:          null,   // null | 'active' | 'inactive'
  activityDays:      30,     // window for the activity filter
  ttp:               null,   // { id, label } | null
  tool:              null,   // { id, label } | null
  first_seen_year:   null,   // number | null
})

// Mirror the widget-relevant filter state up to the view so the stats
// panel can mark the active chart segments
watch(filters, f => {
  emit('filters-change', {
    activity: f.activity,
    country:  f.country,
    ttp:      f.ttp?.id ?? null,
    tool:     f.tool?.id ?? null,
    year:     f.first_seen_year,
  })
}, { deep: true })

// ── Facets (loaded once) ──────────────────────────────────────────────────────
const facets = ref({ countries: [], state_sponsors: [] })

const countryDisplay = (code) => {
  const c = normalizeISO2(code) || code
  const name = regionNameFromISO2(c, [activeLang.value]) || c
  return name && name !== c ? `${name} (${c})` : c
}

const countryOptions = computed(() => [
  { value: null, label: t('hackwatch.groups.filterCountryPlaceholder'), flag: '', count: null },
  ...facets.value.countries.map(c => ({
    value: c.value,
    label: countryDisplay(c.value),
    flag:  flagFromISO2(c.value) || '',
    count: c.count,
  })),
])

const stateSponsorOptions = computed(() => [
  { value: null,                                   label: t('hackwatch.groups.filterStateSponsorPlaceholder'), count: null, group: 'top' },
  { value: '__any__',                              label: t('hackwatch.groups.filterStateSponsorAny'),         count: null, group: 'top' },
  { value: '__none__',                             label: t('hackwatch.groups.filterStateSponsorNone'),        count: null, group: 'top' },
  ...facets.value.state_sponsors.map(s => ({
    value: s.value,
    label: s.value,
    count: s.count,
    group: 'list',
  })),
])

const raasOptions = computed(() => [
  { label: t('hackwatch.groups.filterRaasAll'),  value: null  },
  { label: t('hackwatch.groups.filterRaasOnly'), value: true  },
  { label: t('hackwatch.groups.filterRaasNone'), value: false },
])

const ttpsOptions = computed(() => [
  { label: t('hackwatch.groups.filterAny'),         value: null  },
  { label: t('hackwatch.groups.filterHasTtpsYes'),  value: true  },
  { label: t('hackwatch.groups.filterHasTtpsNo'),   value: false },
])

const toolsOptions = computed(() => [
  { label: t('hackwatch.groups.filterAny'),         value: null  },
  { label: t('hackwatch.groups.filterHasToolsYes'), value: true  },
  { label: t('hackwatch.groups.filterHasToolsNo'),  value: false },
])

// ── Active-filter chips ───────────────────────────────────────────────────────
const activeChips = computed(() => {
  const chips = []
  const f = filters.value
  if (f.country) chips.push({
    key:   'country',
    label: `${t('hackwatch.groups.filterCountryLabel')}: ${countryDisplay(f.country)}`,
    icon:  flagFromISO2(f.country) || '',
  })
  if (f.state_sponsor) chips.push({
    key:   'state_sponsor',
    label: `${t('hackwatch.groups.filterStateSponsorLabel')}: ${f.state_sponsor}`,
  })
  if (f.has_state_sponsor === true) chips.push({
    key:   'has_state_sponsor',
    label: t('hackwatch.groups.filterStateSponsorAny'),
  })
  if (f.has_state_sponsor === false) chips.push({
    key:   'has_state_sponsor',
    label: t('hackwatch.groups.filterStateSponsorNone'),
  })
  if (f.is_raas === true)  chips.push({ key: 'is_raas',  label: t('hackwatch.groups.filterRaasOnly') })
  if (f.is_raas === false) chips.push({ key: 'is_raas',  label: t('hackwatch.groups.filterRaasNone') })
  if (f.has_ttps  === true)  chips.push({ key: 'has_ttps',  label: t('hackwatch.groups.filterHasTtpsYes')  })
  if (f.has_ttps  === false) chips.push({ key: 'has_ttps',  label: t('hackwatch.groups.filterHasTtpsNo')   })
  if (f.has_tools === true)  chips.push({ key: 'has_tools', label: t('hackwatch.groups.filterHasToolsYes') })
  if (f.has_tools === false) chips.push({ key: 'has_tools', label: t('hackwatch.groups.filterHasToolsNo')  })
  if (f.activity) chips.push({
    key:   'activity',
    label: `${t('hackwatch.groups.chipActivity')}: ${f.activity === 'active' ? t('hackwatch.groups.statsActive') : t('hackwatch.groups.statsInactive')} · ${f.activityDays}d`,
  })
  if (f.ttp)  chips.push({ key: 'ttp',  label: `${t('hackwatch.groups.chipTtp')}: ${f.ttp.label}` })
  if (f.tool) chips.push({ key: 'tool', label: `${t('hackwatch.groups.chipTool')}: ${f.tool.label}` })
  if (f.first_seen_year) chips.push({
    key:   'first_seen_year',
    label: `${t('hackwatch.groups.chipFirstSeen')}: ${f.first_seen_year}`,
  })
  return chips
})

const hasAnyFilter = computed(() => activeChips.value.length > 0)

function resetFilters() {
  filters.value = {
    country: null, state_sponsor: null, has_state_sponsor: null,
    is_raas: null, has_ttps: null, has_tools: null,
    activity: null, activityDays: filters.value.activityDays,
    ttp: null, tool: null, first_seen_year: null,
  }
  onFilterChange()
}

// Applied from the stats widgets (chart segment click) — toggle semantics:
// clicking the active value again clears that filter. For the activity
// dimension a changed time window updates the filter instead of toggling.
function applyExternalFilter({ dimension, value, label, days }) {
  const f = filters.value
  if (dimension === 'activity') {
    if (f.activity === value && f.activityDays === days) {
      f.activity = null
    } else {
      f.activity = value
      if (days) f.activityDays = days
    }
  } else if (dimension === 'country') {
    f.country = f.country === value ? null : value
  } else if (dimension === 'ttp') {
    f.ttp = f.ttp?.id === value ? null : { id: value, label }
  } else if (dimension === 'tool') {
    f.tool = f.tool?.id === value ? null : { id: value, label }
  } else if (dimension === 'year') {
    f.first_seen_year = f.first_seen_year === value ? null : value
  } else {
    return
  }
  onFilterChange()
}

defineExpose({ applyExternalFilter })

function removeChip(key) {
  if (key === 'has_state_sponsor') {
    filters.value.has_state_sponsor = null
  } else {
    filters.value[key] = null
  }
  onFilterChange()
}

// ── Sponsor-Select special handling ───────────────────────────────────────────
function onSponsorChange(val) {
  if (val === '__any__') {
    filters.value.state_sponsor     = null
    filters.value.has_state_sponsor = true
  } else if (val === '__none__') {
    filters.value.state_sponsor     = null
    filters.value.has_state_sponsor = false
  } else {
    filters.value.state_sponsor     = val
    filters.value.has_state_sponsor = null
  }
  onFilterChange()
}

const sponsorSelectValue = computed(() => {
  if (filters.value.state_sponsor) return filters.value.state_sponsor
  if (filters.value.has_state_sponsor === true)  return '__any__'
  if (filters.value.has_state_sponsor === false) return '__none__'
  return null
})

// ── Columns ───────────────────────────────────────────────────────────────────
const columns = [
  { field: 'name',             header: t('hackwatch.groups.colName'),         sortable: true,  default: true  },
  { field: 'victim_count',     header: t('hackwatch.groups.colVictims'),      sortable: true,  default: true  },
  { field: 'country',          header: t('hackwatch.groups.colCountry'),      sortable: true,  default: true  },
  { field: 'state_sponsor',    header: t('hackwatch.groups.colStateSponsor'), sortable: true,  default: true  },
  { field: 'ttps_links_count', header: t('hackwatch.groups.colTtps'),         sortable: true,  default: true  },
  { field: 'tools_links_count',header: t('hackwatch.groups.colTools'),        sortable: true,  default: true  },
  { field: 'updated_at',       header: t('hackwatch.groups.colUpdated'),      sortable: true,  default: true  },
  { field: 'first_seen',       header: t('hackwatch.groups.colFirstSeen'),    sortable: true,  default: false },
  { field: 'id',               header: t('hackwatch.groups.colId'),           sortable: true,  default: false },
]

// ── Data loading ──────────────────────────────────────────────────────────────
async function load(q = {}) {
  lastQuery = q
  loading.value = true
  try {
    const params = {
      page:    q.page    ?? 1,
      limit:   q.limit   ?? 25,
      q:       q.search  || undefined,
      sortBy:  q.sortField || 'victim_count',
      sortDir: q.sortOrder === 'asc' ? 'asc' : 'desc',
    }
    const f = filters.value
    if (f.is_raas           !== null) params.is_raas           = f.is_raas
    if (f.country           !== null) params.country           = f.country
    if (f.state_sponsor     !== null) params.state_sponsor     = f.state_sponsor
    if (f.has_state_sponsor !== null) params.has_state_sponsor = f.has_state_sponsor
    if (f.has_ttps          !== null) params.has_ttps          = f.has_ttps
    if (f.has_tools         !== null) params.has_tools         = f.has_tools
    if (f.activity          !== null) {
      params.active             = f.activity === 'active'
      params.active_within_days = f.activityDays
    }
    if (f.ttp               !== null) params.ttp_id            = f.ttp.id
    if (f.tool              !== null) params.tool_id           = f.tool.id
    if (f.first_seen_year   !== null) params.first_seen_year   = f.first_seen_year

    const { data } = await api.getGroupsOverview(params)
    items.value = data.rows ?? []
    total.value = data.total ?? 0
  } catch (e) {
    notify.error(t('common.error'), e.response?.data?.message || t('hackwatch.groups.loadFailed'))
  } finally {
    loading.value = false
  }
}

async function loadFacets() {
  try {
    const { data } = await api.getGroupsFacets()
    facets.value = {
      countries:      data.countries      ?? [],
      state_sponsors: data.state_sponsors ?? [],
    }
  } catch {
    // facets are optional — silently fall back to empty lists
  }
}

function onQueryChange(q) { load(q) }
function onFilterChange()  { load({ ...lastQuery, page: 1 }) }

onMounted(() => {
  loadFacets()
  load()
})

// ── Dialog ────────────────────────────────────────────────────────────────────
const showDialog   = ref(false)
const isAdding     = ref(false)
const saving       = ref(false)
const editingGroup = ref({ name: '', description: '' })

const openCreate = () => {
  isAdding.value = true
  editingGroup.value = { name: '', description: '' }
  showDialog.value = true
}
const openEdit = (row) => {
  isAdding.value = false
  editingGroup.value = { id: row.id, name: row.name ?? '', description: row.description ?? '' }
  showDialog.value = true
}

const saveGroup = async () => {
  if (!editingGroup.value.name?.trim()) {
    notify.warn(t('common.warning'), t('hackwatch.groups.nameMissing'))
    return
  }
  saving.value = true
  try {
    const { id, ...payload } = editingGroup.value
    if (isAdding.value) {
      await api.createHackerGroup(payload)
      notify.success(t('hackwatch.groups.saved'), t('hackwatch.groups.groupCreated'))
    } else {
      await api.updateHackerGroup(id, payload)
      notify.success(t('hackwatch.groups.saved'), t('hackwatch.groups.groupUpdated'))
    }
    showDialog.value = false
    await load(lastQuery)
    await loadFacets()
  } catch (e) {
    notify.error(t('hackwatch.groups.saveFailed'), e.response?.data?.message || e.message)
  } finally {
    saving.value = false
  }
}

const deleteGroups = async (rows) => {
  if (!await confirmDelete(t('hackwatch.groups.confirmDelete'))) return
  try {
    await Promise.all(rows.map(r => api.deleteHackerGroup(r.id)))
    notify.success(t('hackwatch.groups.deleted'), t('hackwatch.groups.groupDeleted'))
    await load(lastQuery)
    await loadFacets()
  } catch (e) {
    notify.error(t('hackwatch.groups.deleteFailed'), e.response?.data?.message || e.message)
  }
}

const tableActions = computed(() => [
  user.can('hackwatch_groups.change') && {
    label:          t('hackwatch.groups.editTooltip'),
    icon:           'pi pi-pencil',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('hackwatch_groups.delete') && {
    label:     t('hackwatch.groups.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   deleteGroups,
  },
].filter(Boolean))
</script>

<template>
  <div class="groups-overview">
    <!-- ── Filterleiste ──────────────────────────────────────────────────── -->
    <div class="hw-card filter-card">
      <div class="hw-card-header">
        <i class="pi pi-filter hw-card-icon" />
        <span class="hw-card-title">{{ t('hackwatch.groups.filtersTitle') }}</span>
        <pButton
          v-if="hasAnyFilter"
          icon="pi pi-times"
          :label="t('hackwatch.groups.filterReset')"
          size="small"
          severity="secondary"
          text
          @click="resetFilters"
        />

      </div>

      <div class="filter-grid">
        <!-- Country -->
        <div class="filter-field">
          <label>{{ t('hackwatch.groups.filterCountryLabel') }}</label>
          <Select
            v-model="filters.country"
            :options="countryOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.groups.filterCountryPlaceholder')"
            :filter="true"
            :filter-placeholder="t('hackwatch.groups.filterCountrySearch')"
            show-clear
            class="w-full"
            @change="onFilterChange"
          >
            <template #value="slotProps">
              <span v-if="slotProps.value" class="filter-select-value">
                <span v-if="flagFromISO2(slotProps.value)" class="flag">{{ flagFromISO2(slotProps.value) }}</span>
                <span>{{ countryDisplay(slotProps.value) }}</span>
              </span>
              <span v-else class="text-muted">{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <span class="filter-option-row">
                <span v-if="slotProps.option.flag" class="flag">{{ slotProps.option.flag }}</span>
                <span class="filter-option-label">{{ slotProps.option.label }}</span>
                <span v-if="slotProps.option.count != null" class="filter-option-count">{{ slotProps.option.count }}</span>
              </span>
            </template>
          </Select>
        </div>

        <!-- State Sponsor -->
        <div class="filter-field">
          <label>{{ t('hackwatch.groups.filterStateSponsorLabel') }}</label>
          <Select
            :model-value="sponsorSelectValue"
            :options="stateSponsorOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.groups.filterStateSponsorPlaceholder')"
            :filter="true"
            show-clear
            class="w-full"
            @change="onSponsorChange($event.value)"
          >
            <template #option="slotProps">
              <span class="filter-option-row">
                <span class="filter-option-label">{{ slotProps.option.label }}</span>
                <span v-if="slotProps.option.count != null" class="filter-option-count">{{ slotProps.option.count }}</span>
              </span>
            </template>
          </Select>
        </div>

        <!-- RaaS -->
        <div class="filter-field">
          <label>{{ t('hackwatch.groups.filterRaasLabel') }}</label>
          <Select
            v-model="filters.is_raas"
            :options="raasOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.groups.filterRaasAll')"
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Has TTPs -->
        <div class="filter-field">
          <label>{{ t('hackwatch.groups.filterHasTtps') }}</label>
          <Select
            v-model="filters.has_ttps"
            :options="ttpsOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.groups.filterAny')"
            class="w-full"
            @change="onFilterChange"
          />
        </div>

        <!-- Has Tools -->
        <div class="filter-field">
          <label>{{ t('hackwatch.groups.filterHasTools') }}</label>
          <Select
            v-model="filters.has_tools"
            :options="toolsOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('hackwatch.groups.filterAny')"
            class="w-full"
            @change="onFilterChange"
          />
        </div>
      </div>

      <!-- Active filter chips -->
      <div v-if="hasAnyFilter" class="filter-chips">
        <Chip
          v-for="chip in activeChips"
          :key="chip.key + chip.label"
          :label="chip.icon ? `${chip.icon} ${chip.label}` : chip.label"
          removable
          @remove="removeChip(chip.key)"
        />
      </div>
    </div>

    <!-- ── Tabelle ───────────────────────────────────────────────────────── -->
    <AppTable
      table-key="hackwatch-groups"
      :columns="columns"
      :data="items"
      :total="total"
      :loading="loading"
      :actions="tableActions"
      lazy
      @refresh="load(lastQuery)"
      @query-change="onQueryChange"
    >
      <template #toolbar-left>
        <pButton
          v-if="user.can('hackwatch_groups.create')"
          icon="pi pi-plus"
          :label="t('hackwatch.groups.addBtn')"
          size="small"
          @click="openCreate"
        />
      </template>

      <!-- Name + RaaS-Badge -->
      <template #cell-name="{ row }">
        <div class="flex align-items-center gap-2">
          <router-link :to="`/threat-intel/actors/${row.id}`" class="hw-link">
            <strong>{{ row.name }}</strong>
          </router-link>
          <Tag
            v-if="row.is_raas"
            :value="t('hackwatch.groups.badgeRaas')"
            severity="danger"
            style="font-size: 0.7rem; padding: 0.1rem 0.4rem;"
          />
        </div>
      </template>

      <!-- Opferanzahl -->
      <template #cell-victim_count="{ row }">
        <span v-if="row.victim_count">{{ row.victim_count.toLocaleString() }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Land -->
      <template #cell-country="{ row }">
        <span v-if="row.country" class="flex align-items-center gap-1">
          <span v-if="flagFromISO2(row.country)" class="flag">{{ flagFromISO2(row.country) }}</span>
          <span>{{ countryDisplay(row.country) }}</span>
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- State Sponsor -->
      <template #cell-state_sponsor="{ row }">
        <span v-if="row.state_sponsor">{{ row.state_sponsor }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- TTP-Count -->
      <template #cell-ttps_links_count="{ row }">
        <Tag
          :value="String(row.ttps_links_count ?? 0)"
          :severity="(row.ttps_links_count ?? 0) > 0 ? 'warn' : 'secondary'"
          style="font-size: 0.75rem;"
        />
      </template>

      <!-- Tool-Count -->
      <template #cell-tools_links_count="{ row }">
        <Tag
          :value="String(row.tools_links_count ?? 0)"
          severity="secondary"
          style="font-size: 0.75rem;"
        />
      </template>

      <!-- Datum -->
      <template #cell-updated_at="{ row }">{{ fmtDate(row.updated_at) }}</template>
      <template #cell-first_seen="{ row }">{{ fmtDate(row.first_seen) }}</template>

    </AppTable>

    <!-- Dialog -->
    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('hackwatch.groups.dialogCreate') : t('hackwatch.groups.dialogEdit')"
      size="md"
      :show-footer="true"
      :maximizable="false"
      :loading="saving"
      @submit="saveGroup"
      @cancel="showDialog = false"
    >
      <div class="dialog-form">
        <div class="form-field required">
          <label>{{ t('hackwatch.groups.formName') }}</label>
          <InputText
            v-model.trim="editingGroup.name"
            class="w-full"
            :placeholder="t('hackwatch.groups.formNamePlaceholder')"
          />
        </div>
        <div class="form-field">
          <label>{{ t('hackwatch.groups.formDescription') }}</label>
          <Textarea
            v-model.trim="editingGroup.description"
            rows="5"
            class="w-full"
            :placeholder="t('hackwatch.groups.formDescriptionPlaceholder')"
          />
        </div>
      </div>
    </AppDialog>
  </div>
</template>

<style scoped>
.groups-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hw-link {
  color: var(--p-text-color);
  text-decoration: none;
}
.hw-link:hover {
  color: var(--p-primary-color);
  text-decoration: underline;
}

.flag {
  font-size: 1.1rem;
  line-height: 1;
}

/* ── Filter-Karte ───────────────────────────────────────────────────────── */
.filter-card { padding-bottom: 12px; }

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px 14px;
  padding: 8px 14px 4px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filter-field label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-color);
}

.filter-select-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.filter-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.filter-option-label { flex: 1; }
.filter-option-count {
  font-size: 0.75rem;
  color: var(--p-text-color-secondary);
  background: var(--p-surface-100);
  padding: 1px 6px;
  border-radius: 10px;
}
:global(.my-app-dark) .filter-option-count {
  background: var(--p-surface-700);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 14px 0;
}
.filter-chips :deep(.p-chip) {
  font-size: 0.78rem;
  padding: 2px 8px;
}
</style>
